import React from "react";
import type { OutputField, OutputSchema, OutputType } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  outputType: OutputType;
  outputSchema: OutputSchema;
  outputTypes: Record<OutputType, string>;
  outputSchemas: Record<OutputSchema, { label: string; description: string; fields: OutputField[] }>;
  selectedOutputFieldRows: OutputField[];
  outputPreview: string;
}

export default function OutputPreviewModal({
  open,
  onClose,
  outputType,
  outputSchema,
  outputTypes,
  outputSchemas,
  selectedOutputFieldRows,
  outputPreview,
}: Props) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6 backdrop-blur-sm">
      <div className="max-h-[88vh] w-full max-w-4xl overflow-hidden rounded-2xl border border-white/10 bg-[#081322] shadow-lg shadow-black/10">
        <div className="flex items-center justify-between border-b border-white/10 bg-[#0b1627] px-6 py-4">
          <div>
            <div className="text-lg font-black text-white">Output Preview</div>
            <div className="mt-1 text-xs text-slate-500">
              {outputTypes[outputType]} · {outputSchemas[outputSchema].label} · {selectedOutputFieldRows.length} fields
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 bg-[#101c2f] px-4 py-2 text-sm font-bold text-slate-300 hover:bg-white/10"
          >
            Close
          </button>
        </div>

        <div className="grid max-h-[calc(88vh-73px)] grid-cols-1 gap-4 overflow-auto p-6 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="space-y-3">
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
              <div className="text-xs font-black uppercase tracking-wide text-emerald-200">Output contract</div>
              <div className="mt-2 text-sm font-bold text-white">{outputTypes[outputType]}</div>
              <div className="mt-1 text-xs text-emerald-100/70">{outputSchemas[outputSchema].description}</div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#101c2f] p-4">
              <div className="text-xs font-black uppercase tracking-wide text-slate-500">Selected fields</div>
              <div className="mt-3 max-h-72 space-y-2 overflow-auto">
                {selectedOutputFieldRows.map((field) => (
                  <div key={field.key} className="rounded-lg bg-[#071120] px-3 py-2">
                    <div className="text-xs font-bold text-white">{field.label}</div>
                    <div className="mt-1 truncate font-mono text-[11px] text-slate-500">{field.key}</div>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          <main>
            <div className="mb-3 text-sm font-black text-white">Generated output example</div>
            <pre className="max-h-[60vh] overflow-auto break-words whitespace-pre-wrap rounded-2xl border border-white/10 bg-[#071120] p-5 font-mono text-xs leading-5 text-blue-100">
              {outputPreview || "No fields selected"}
            </pre>
          </main>
        </div>
      </div>
    </div>
  );
}
